/**
 * Shared vocabulary for the sentencing workspace: where a sentence is known from, what an
 * appeal did to it, and how terms, statistics and Browse links are written.
 *
 * One pool of sentences (owner, 2026-10-10: "a sentence is a sentence"). A sentence is
 * known from DOC's records, from a Montana Supreme Court opinion or Sentence Review decision
 * that DOC does not hold, or from both (a DOC sentencing an opinion also reports, one row).
 */

export const KNOWN_FROM = [
  { key: 'doc', label: 'DOC', long: 'Known from DOC' },
  { key: 'both', label: 'DOC + opinion', long: 'Known from DOC and an opinion' },
  { key: 'opinion', label: 'Opinion', long: 'Known from an opinion only' },
];

export const KNOWN_FROM_LABELS = Object.fromEntries(KNOWN_FROM.map((k) => [k.key, k.label]));

export function knownFromLabel(value) {
  return KNOWN_FROM_LABELS[value] || value || '—';
}

/** Old links and cards carried `source`; a row's known_from is the word now. */
export function knownFromOf(rec) {
  if (rec?.known_from) return rec.known_from;
  return rec?.source === 'appellate' || rec?.source === 'opinion' ? 'opinion' : 'doc';
}

export const KNOWN_FROM_NOTE =
  'DOC lists the people under its supervision; an opinion reports the sentence of a case that was appealed. '
  + 'A sentence DOC holds that an opinion also reports is one row, known from both.';

// What the appeal did to the sentence (opinion_sentence.after_appeal).
export const AFTER_APPEAL_LABELS = {
  stands: 'Stands',
  part_corrected: 'Part corrected',
  conviction_reversed: 'Conviction reversed',
  resentencing_ordered: 'Resentencing ordered',
  revocation_reversed: 'Revocation reversed',
  modified_on_review: 'Modified on review',
  unknown: 'Not stated',
};

// Whether a row's fate on appeal is its own count's or the case's (record.fate_scope).
export const FATE_SCOPE_NOTES = {
  count: 'What the appeal did to this count',
  order: "What the appeal did across the counts of this case: the opinion's counts could not be matched to this charge by statute",
};

export const OPINION_KIND_LABELS = {
  opinion: 'Supreme Court opinion',
  srd_decision: 'Sentence Review decision',
};

export const STAGE_LABELS = {
  original: 'Original sentence',
  revocation: 'Revocation sentence',
  resentencing: 'Resentencing',
  amended: 'Amended sentence',
  unknown: 'Stage not stated',
};

// Where the opinion says the sentence is served.
export const PLACE_LABELS = {
  MSP: 'Montana State Prison',
  MWP: "Montana Women's Prison",
  DOC: 'Department of Corrections',
  county_jail: 'County jail',
  fine_only: 'Fine only',
  life: 'Life',
  death: 'Death',
  other: 'Other',
  unknown: 'Place not stated',
};

// DOC's offense modifiers: the circumstances every DOC row can answer.
export const MODIFIER_LABELS = {
  weapon: 'Weapon',
  pfo: 'Persistent felony offender',
  attempt: 'Attempt',
  conspiracy: 'Conspiracy',
  accountability: 'Accountability',
  solicitation: 'Solicitation',
};

// A section the opinion does not cite: DOC's section for that offense name.
export const SECTION_FROM_NAME_NOTE =
  'The opinion cites no section for this offense; this is the section DOC uses for the offense name.';

// DOC's export changes: a later export can change an order it listed before, and stops
// listing a person who has left DOC. Nothing is thrown out: the row shows DOC's latest, says
// that it changed, and the earlier versions stay readable (owner ruling 2026-10-04).
export const DOC_CHANGE_FIELD_LABELS = {
  judge: 'judge',
  sentence_type: 'sentence type',
  total_term: 'total term',
  commitment: 'commitment',
  suspension: 'suspension',
};

/** "total_term" → "total term"; "offenses (3 → 1)" reads as it is. */
export function docChangeLabel(field) {
  return DOC_CHANGE_FIELD_LABELS[field] || field;
}

/** "2026-10-01" → "Oct 1, 2026" (a bare date, never shifted by a time zone). */
export function exportDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return iso || '';
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1];
  return `${month} ${Number(m[3])}, ${m[1]}`;
}

export function docChangedNote(history) {
  return `DOC changed this sentence in its export of ${exportDate(history?.changed_in)}. `
    + 'This row is DOC\'s latest; open it to see what DOC published before.';
}

/** A docket can hold several sentencings; the note names the one it is about. */
export function docNotListedNote(history, imposedOn) {
  return `The sentence imposed ${imposedOn || 'on an unknown date'} is not in DOC's current export `
    + `(last listed ${exportDate(history?.last_published)}). `
    + 'DOC\'s export lists people currently under DOC; the record is kept as DOC last published it.';
}

export function afterAppealLabel(value) {
  return AFTER_APPEAL_LABELS[value] || value || '—';
}

/** 120 → "120 mo (10 y)"; 0.5 → "0.5 mo"; null → "—". */
export function formatMonths(months) {
  if (months == null || months === '') return '—';
  const n = Number(months);
  if (!Number.isFinite(n)) return '—';
  const rounded = Number.isInteger(n) ? n : Math.round(n * 10) / 10;
  if (n >= 12 && Number.isInteger(n / 12)) return `${rounded} mo (${n / 12} y)`;
  return `${rounded} mo`;
}

const roundMonths = (n) => (Number.isInteger(n) ? n : Math.round(n * 10) / 10).toLocaleString();

/**
 * 36 → "36 mo"; null → "—" (a pill or a tile; the years go in its title or note). A term of a
 * century or more in whole years reads in years: DOC enters a life-type sentence as 999 years
 * (11,988 months), and "999 y" says that at a glance.
 */
export function monthsShort(months) {
  if (months == null || months === '') return '—';
  const n = Number(months);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1200 && Number.isInteger(n / 12)) return `${(n / 12).toLocaleString()} y`;
  return `${roundMonths(n)} mo`;
}

/** "36 – 108 mo" (a tile's value); null → "—". */
export function monthsRange(a, b) {
  const x = Number(a);
  const y = Number(b);
  if (a == null || b == null || !Number.isFinite(x) || !Number.isFinite(y)) return '—';
  return `${roundMonths(x)} – ${roundMonths(y)} mo`;
}

/** The same figures in years, "3 – 9 y" or "5 y"; '' under a year. */
export function yearsNote(...values) {
  const nums = values.filter((v) => v != null && v !== '').map(Number);
  if (nums.length === 0 || nums.some((n) => !Number.isFinite(n)) || Math.max(...nums) < 12) return '';
  const y = (n) => { const v = n / 12; return (Number.isInteger(v) ? v : Math.round(v * 10) / 10).toLocaleString(); };
  return nums.length === 1 ? `${y(nums[0])} y` : `${y(nums[0])} – ${y(nums[1])} y`;
}

/** 1 → "1 sentence", 2 → "2 sentences" (the number with thousands separators). */
export function countOf(n, singular, plural = `${singular}s`) {
  const value = Number(n ?? 0);
  return `${value.toLocaleString()} ${value === 1 ? singular : plural}`;
}

/** "54,370 from DOC · 1,458 from DOC and an opinion · 3,793 from an opinion only" */
export function mixLine(mix, sep = ' · ') {
  if (!mix) return '';
  const parts = [];
  if (mix.doc?.rows) parts.push(`${Number(mix.doc.rows).toLocaleString()} from DOC`);
  if (mix.both?.rows) parts.push(`${Number(mix.both.rows).toLocaleString()} from DOC and an opinion`);
  if (mix.opinion?.rows) parts.push(`${Number(mix.opinion.rows).toLocaleString()} from an opinion only`);
  return parts.join(sep);
}

const LEFT_OUT_BECAUSE = {
  judges: 'judge', counties: 'county', sentencing_years: 'year', sentencing_year_min: 'year', sentencing_year_max: 'year',
  imposed_on_dates: 'date', age_at_sentencing_min: 'age', age_at_sentencing_max: 'age', sexes: 'sex', races: 'race',
  birth_years: 'birth year', facts_query: 'facts', after_appeal: 'fate on appeal', exclude_conviction_reversed: 'fate on appeal',
  opinion_kinds: 'opinion', sentence_stages: 'stage', term_months_min: 'term', term_months_max: 'term',
  total_term_months_min: 'total term', total_term_months_max: 'total term', commit_types: 'commitment', suspensions: 'suspension',
  mca_codes: 'section', procedural_sentence_types: 'sentence type', dockets: 'docket', descriptions: 'offense', names: 'name',
  modifiers_weapon: 'weapon modifier', modifiers_pfo: 'PFO modifier', modifiers_attempt: 'attempt modifier',
  modifiers_conspiracy: 'conspiracy modifier', offense_codes: 'offense code', doc_ids: 'DOC id',
};

/**
 * "38 more known from an opinion have no judge on record and are left out" — the rows a
 * filter leaves out only because they cannot answer it (the server's left_out).
 */
export function leftOutLines(leftOut) {
  const lines = [];
  for (const [source, info] of Object.entries(leftOut || {})) {
    if (!info?.count) continue;
    const fields = [...new Set((info.because || []).map((b) => LEFT_OUT_BECAUSE[b] || b))];
    const who = source === 'opinion' ? 'known from an opinion' : 'known from DOC';
    lines.push(`${Number(info.count).toLocaleString()} more ${who} have no ${fields.join(' or ') || 'answer'} on record and are left out`);
  }
  return lines;
}

// The opinion's paragraphs are shown as the Court published them (owner, 2026-10-04: names
// that are publicly available in the opinion are not hidden); the label says whose words they are.
export const COURT_WORDS_NOTE = "The Court's own words, as published";

/** The cite as a table cell shows it: a Sentence Review decision as "SRD 2013-03-06". */
export function shortCite(opinion) {
  if (!opinion) return '';
  if (opinion.kind === 'srd_decision') return `SRD${opinion.filed_date ? ` ${opinion.filed_date}` : ''}`;
  return opinion.cite || '';
}

/** A {value, unit} term as text. */
export function formatTerm(term) {
  if (term == null) return '—';
  if (typeof term === 'object') {
    const value = term.value ?? term.length ?? null;
    if (value == null) return '—';
    return `${value} ${term.unit || ''}`.trim();
  }
  return String(term);
}

/**
 * A link that opens Browse on a filter set — how a chat answer hands the
 * reader the filter that produced it. `filters` uses the API's filter names.
 */
export function browseLink(filters) {
  return `/sentencing?mode=browse&f=${encodeURIComponent(JSON.stringify({ ...(filters || {}) }))}`;
}

/** The inverse of browseLink, for Browse's first render. Never throws. */
export function parseBrowseParams(search) {
  try {
    const params = new URLSearchParams(search || '');
    const raw = params.get('f');
    const filters = raw ? JSON.parse(raw) : {};
    if (!filters || typeof filters !== 'object' || Array.isArray(filters)) return { mode: params.get('mode'), filters: {} };
    return { mode: params.get('mode'), filters };
  } catch {
    return { mode: null, filters: {} };
  }
}

const FILTER_LABELS = {
  counties: 'County',
  judges: 'Judge',
  mca_codes: 'MCA §',
  mca_codes_count2: '2nd charge',
  last_names: 'Name',
  first_names: 'First name',
  dockets: 'Docket',
  commit_types: 'Commitment',
  suspensions: 'Suspension',
  procedural_sentence_types: 'Sentence type',
  after_appeal: 'On appeal',
  opinion_kinds: 'Document',
  known_from: 'Known from',
  sexes: 'Sex',
  races: 'Race',
  birth_years: 'Birth year',
  age_at_sentencing_min: 'Age from',
  age_at_sentencing_max: 'Age to',
  sentencing_years: 'Year',
  sentencing_year_min: 'Year from',
  sentencing_year_max: 'Year to',
  term_months_min: 'Term from (mo)',
  term_months_max: 'Term to (mo)',
  total_term_months_min: 'Total term from (mo)',
  total_term_months_max: 'Total term to (mo)',
  imposed_on_dates: 'Imposed',
  offense_codes: 'Offense code',
  facts_query: 'Facts',
  facts_scope: 'Facts in',
  descriptions: 'Offense',
  sentence_types: 'Sentence type (DOC)',
  sentence_stages: 'Stage',
  offense_dates: 'Offense date',
  doc_ids: 'DOC id',
  middle_names: 'Middle name',
  suffixes: 'Suffix',
  montana_residents: 'MT resident',
  birth_states: 'Birth state',
  citizenships: 'Citizenship',
  correctional_statuses: 'Correctional status',
  location_names: 'Facility',
};

/** Filter set → readable chips ("County: Cascade"), for cards that show their filter. */
export function describeFilters(filters) {
  const chips = [];
  for (const [key, value] of Object.entries(filters || {})) {
    // the sort is how the list is ordered, not what it holds
    if (key === 'source' || key === 'sort_by' || key === 'sort_dir' || value == null || value === false || (Array.isArray(value) && value.length === 0)) continue;
    if (key === 'exclude_conviction_reversed') {
      chips.push('Reversed convictions left out');
      continue;
    }
    if (key.startsWith('modifiers_')) {
      const on = Array.isArray(value) ? value.includes(true) : Boolean(value);
      chips.push(`${MODIFIER_LABELS[key.slice('modifiers_'.length)] || key}: ${on ? 'yes' : 'no'}`);
      continue;
    }
    if (key === 'facts_scope') {
      if (value === 'opinion' && filters.facts_query) chips.push('Facts in: the whole opinion');
      continue;
    }
    const label = FILTER_LABELS[key] || key;
    const values = (Array.isArray(value) ? value : [value]).map((v) => (
      key === 'after_appeal' ? afterAppealLabel(v)
        : key === 'opinion_kinds' ? (OPINION_KIND_LABELS[v] || v)
          : key === 'known_from' ? knownFromLabel(v)
            : String(v)
    ));
    chips.push(`${label}: ${values.join(', ')}`);
  }
  return chips;
}
